import { Injectable } from '@angular/core';
import {
  WinesuitePayoutRow,
  WinesuitePayoutsListQuery,
  WinesuitePayoutsPage,
  WSPaymentsService,
} from '@services/dws/ws-payments.service';
import { tr } from '@util/tr';
import { observeNoLoading } from '@web/util/loading/loading';
import { toast } from '@web/util/toast';
import { WsDownloadsService } from '@web/app/services/ws-downloads.service';
import { Observable, Subject, Subscription, of, BehaviorSubject, from } from 'rxjs';
import { concatMap, map, mergeMap, takeUntil, toArray } from 'rxjs/operators';
import {
  buildPayoutListExportFile,
  EXPORT_PAGE_SIZE,
  PayoutsListExportFormat,
} from './payouts-list-export';

export const PAYOUTS_LIST_EXPORT_JOB_ID = 'payouts-list-export';

const LIST_EXPORT_PAGE_CONCURRENCY = 4;

export interface PayoutsListExportRequest {
  wineryId: string;
  query: WinesuitePayoutsListQuery;
  format: PayoutsListExportFormat;
}

interface ListExportProgress {
  current: number;
  total: number;
}

@Injectable({ providedIn: 'root' })
export class PayoutsListExportService {
  readonly running$ = new BehaviorSubject<boolean>(false);
  private runSubscription?: Subscription;
  private cancel$ = new Subject<void>();
  private cancelled = false;
  private pagesCompleted = 0;

  constructor(
    private readonly wsPaymentsService: WSPaymentsService,
    private readonly downloads: WsDownloadsService,
  ) {}

  get isRunning(): boolean {
    return this.running$.value;
  }

  startExport(request: PayoutsListExportRequest): void {
    if (this.runSubscription && !this.runSubscription.closed) {
      return;
    }
    if (this.isRunning) {
      this.finishRunning();
      this.clearRunningDownload();
    }

    this.runSubscription?.unsubscribe();
    this.cancel$ = new Subject<void>();
    this.cancelled = false;
    this.pagesCompleted = 0;
    this.setRunning({ current: 0, total: 1 });
    toast(tr('payments.payouts.exportStarted'));

    this.runSubscription = this.fetchAllPages(request).pipe(
      takeUntil(this.cancel$),
    ).subscribe({
      next: (rows) => {
        if (this.cancelled) {
          return;
        }
        this.finalizeExport(request.format, rows);
      },
      error: (err) => {
        if (this.cancelled) {
          return;
        }
        console.error('[payouts.list.export] Export failed:', err);
        this.finishRunning();
        this.clearRunningDownload();
        toast(tr('payments.payouts.exportError'));
      },
      complete: () => {
        if (this.cancelled) {
          return;
        }
        if (this.isRunning) {
          this.finishRunning();
          this.clearRunningDownload();
        }
      },
    });
  }

  cancelExport(): void {
    if (!this.isRunning) {
      return;
    }

    this.cancelled = true;
    this.cancel$.next();
    this.cancel$.complete();
    this.runSubscription?.unsubscribe();
    this.runSubscription = undefined;
    this.finishRunning();
    this.clearRunningDownload();
    toast(tr('payments.payouts.exportCancelled'));
  }

  private fetchAllPages(request: PayoutsListExportRequest): Observable<WinesuitePayoutRow[]> {
    return observeNoLoading(
      this.wsPaymentsService.getWinesuitePayouts(
        request.wineryId,
        0,
        EXPORT_PAGE_SIZE,
        request.query,
      ),
    ).pipe(
      concatMap((firstPage) => {
        const totalElements = firstPage?.totalElements ?? 0;
        const totalPages = Math.max(1, Math.ceil(totalElements / EXPORT_PAGE_SIZE));
        const firstRows = this.rowsFromPayoutsPage(firstPage);
        this.pagesCompleted = 1;
        this.setRunning({ current: this.pagesCompleted, total: totalPages });

        if (totalPages <= 1) {
          return of(firstRows);
        }

        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);
        return from(remainingPages).pipe(
          mergeMap(
            (page) => observeNoLoading(
              this.wsPaymentsService.getWinesuitePayouts(
                request.wineryId,
                page,
                EXPORT_PAGE_SIZE,
                request.query,
              ),
            ).pipe(
              map((pageResult) => {
                this.pagesCompleted += 1;
                this.setRunning({ current: this.pagesCompleted, total: totalPages });
                return this.rowsFromPayoutsPage(pageResult);
              }),
            ),
            LIST_EXPORT_PAGE_CONCURRENCY,
          ),
          toArray(),
          map((rest) => firstRows.concat(...rest)),
        );
      }),
    );
  }

  private rowsFromPayoutsPage(page: WinesuitePayoutsPage | null | undefined): WinesuitePayoutRow[] {
    return page?.payouts ?? [];
  }

  private finalizeExport(format: PayoutsListExportFormat, rows: WinesuitePayoutRow[]): void {
    this.finishRunning();

    if (!rows.length) {
      this.clearRunningDownload();
      toast(tr('payments.payouts.exportNoData'));
      return;
    }

    const built = buildPayoutListExportFile(rows, format);
    if (!built) {
      this.clearRunningDownload();
      toast(tr('payments.payouts.exportNoData'));
      return;
    }

    this.downloads.completeDownload(PAYOUTS_LIST_EXPORT_JOB_ID, {
      id: PAYOUTS_LIST_EXPORT_JOB_ID,
      label: tr('payments.payouts.exportReady'),
      hint: built.filename,
      blob: built.blob,
      filename: built.filename,
    });
    this.downloads.triggerDownload(PAYOUTS_LIST_EXPORT_JOB_ID);
    toast(tr('payments.payouts.exportComplete'));
  }

  private setRunning(progress: ListExportProgress): void {
    this.running$.next(true);
    this.downloads.upsertDownload({
      id: PAYOUTS_LIST_EXPORT_JOB_ID,
      label: tr('payments.advancedExport.headerStatus'),
      hint: tr('payments.advancedExport.background'),
      progress,
      status: 'running',
      cancel: () => this.cancelExport(),
    });
  }

  private finishRunning(): void {
    this.running$.next(false);
    this.runSubscription = undefined;
  }

  private clearRunningDownload(): void {
    const existing = this.downloads.downloads$.value.find((entry) => entry.id === PAYOUTS_LIST_EXPORT_JOB_ID);
    if (existing?.status === 'running') {
      this.downloads.removeDownload(PAYOUTS_LIST_EXPORT_JOB_ID);
    }
  }
}
